import * as React from "react";
import {useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {AppFrame} from "@/app/AppFrame";
import {CircularProgress, Grid2 as Grid, Grid2, Typography} from "@mui/material";
import {useModelState} from "@/lib/modelstate";
import {UserSignUpDTO, UserStatus} from "@/api/data-contracts";
import {authenticationApi} from "@/boxhub-api";
import {AxiosError} from "axios";
import {FinishedPage, FinishedPageProps} from "@/common/FinishedPage";
import {useTranslation} from "react-i18next";
import {AuthI18N, UserI18N} from "@/translations";
import * as yup from "yup";
import {Constants} from "@/api/constants";
import {PASSWORD_REGEX} from "@/regex";
import {CardEx} from "@/lib/components/CardEx";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {AppPaths} from "@/app/AppPaths";
import {encodePasswordBase64} from "@/lib/utils";

export const UserSignUpPage = () => {
    const { t } = useTranslation();
    const params = useParams();
    const [skipUnsavedCheck, setSkipUnsavedCheck] = useState(false);
    const navigate = useNavigate();
    const [confirmSaving, setConfirmSaving] = useState(false);

    const [finishedInfo, setFinishedInfo] = React.useState<FinishedPageProps | null>(null);

    const isInvited = (m: UserSignUpDTO | undefined): boolean => m?.userStatus === UserStatus.INVITED;

    const schema = yup.object().shape({
        password: yup.string().when("userStatus", {
            is: UserStatus.INVITED,
            then: (s) =>
                s
                        .required(t(UserI18N.form.validation.passwordRequired))
                    .min(
                        Constants.PASSWORD_LENGTH_MIN,
                            t(UserI18N.form.validation.passwordMin, {min: Constants.PASSWORD_LENGTH_MIN})
                    )
                    .max(
                        Constants.PASSWORD_LENGTH_MAX,
                            t(UserI18N.form.validation.passwordMax, {max: Constants.PASSWORD_LENGTH_MAX})
                    )
                        .matches(PASSWORD_REGEX, t(UserI18N.form.validation.passwordInvalid)),
            otherwise: (s) => s.notRequired()
        }),

        confirmPassword: yup.string().when("userStatus", {
            is: UserStatus.INVITED,
            then: (s) =>
                s
                        .required(t(UserI18N.form.validation.passwordRequired))
                    .min(
                        Constants.PASSWORD_LENGTH_MIN,
                            t(UserI18N.form.validation.passwordMin, {min: Constants.PASSWORD_LENGTH_MIN})
                    )
                    .max(
                        Constants.PASSWORD_LENGTH_MAX,
                            t(UserI18N.form.validation.passwordMax, {max: Constants.PASSWORD_LENGTH_MAX})
                    )
                        .oneOf([yup.ref("password")], t(UserI18N.form.validation.passwordsMustMatch)),
            otherwise: (s) => s.notRequired()
        })
    }) as yup.ObjectSchema<UserSignUpDTO>;

    const onLoadError = (error: AxiosError<any>) => {
        setFinishedInfo({
            success: false,
            title: t(AuthI18N.userSignup.message.failedTitle),
            subTitle: error.response?.data?.error?.message ?? t(AuthI18N.userSignup.message.failedSubtitle)
        });
        return true;
    };

    const { model, errors, onChange, status, save, isDirty } = useModelState<UserSignUpDTO, string>({
        id: params.token,
        schema,
        loader: authenticationApi.beginUserSignUp,
        updater: authenticationApi.performUserSignUp,
        onLoadError,
        sendTransformer: (model) => ({
            ...model,
            password: encodePasswordBase64(model.password),
            confirmPassword: encodePasswordBase64(model.confirmPassword)
        })
    });

    const handleSubmit = () => {
        setSkipUnsavedCheck(true);

        save()?.then(() => {
            navigate(AppPaths.SIGN_IN);
        });
    };

    const handleConfirm = () => {
        if (!params.token || !model) return;
        setConfirmSaving(true);
        authenticationApi
            .performUserSignUp(params.token, model)
            .then(() => {
                setFinishedInfo({
                    success: true,
                    title: t(AuthI18N.confirmCompany.message.successTitle),
                    subTitle: t(AuthI18N.confirmCompany.message.successSubtitle)
                });
            })
            .catch((err) => {
                setFinishedInfo({
                    success: false,
                    title: t(AuthI18N.confirmCompany.message.failedTitle),
                    subTitle:
                            err.response?.data?.error?.message ?? t(AuthI18N.confirmCompany.message.failedSubtitle)
                });
            })
            .finally(() => setConfirmSaving(false));
    };

    if (finishedInfo) {
        return <FinishedPage {...finishedInfo} />;
    }

    return (
        <AppFrame
                title={t(AuthI18N.userSignup.page.title)}
            modelState={status}
            isDirty={isInvited(model) ? !skipUnsavedCheck && isDirty : undefined}
        >
            <Grid2 container spacing={2} paddingTop={1}>
                <Grid2 size={{ xs: 12 }}>
                    <CardEx
                            title={t(AuthI18N.userSignup.page.cardTitle, {
                            companyName: model?.company?.name ?? ""
                        })}
                    >
                        {!isInvited(model) && (
                            <Grid2 container direction="column" spacing={3}>
                                <Grid2>
                                    <Typography variant="body1">
                                        {t(AuthI18N.confirmCompany.page.description, {
                                            companyName: model?.company?.name ?? ""
                                        })}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ sm: 6, md: 4, lg: 3 }}>
                                    <ButtonEx
                                        label={
                                            confirmSaving ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                    t(AuthI18N.confirmCompany.action.confirm)
                                            )
                                        }
                                        fullWidth
                                        onClick={handleConfirm}
                                        disabled={status === "loading" || confirmSaving}
                                        sx={{ mt: 1 }}
                                    />
                                </Grid2>
                            </Grid2>
                        )}
                        {isInvited(model) && (
                            <Grid2 container direction="column" spacing={3}>
                                <Grid2>
                                    <Typography variant="body1">
                                        {t(AuthI18N.userSignup.form.passwordCardTitle)}
                                    </Typography>
                                </Grid2>
                                <Grid2 container direction="row" spacing={2} width="100%">
                                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                                        <StringInputField
                                            name="password"
                                            type="password"
                                            label={t(UserI18N.form.field.password)}
                                            value={model?.password}
                                            error={errors?.password}
                                            maxChars={Constants.PASSWORD_LENGTH_MAX}
                                            onChange={onChange}
                                        />
                                    </Grid>
                                </Grid2>
                                <Grid2 container direction="row" spacing={2} width="100%">
                                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                                        <StringInputField
                                            name="confirmPassword"
                                            type="password"
                                            label={t(UserI18N.form.field.confirmPassword)}
                                            value={model?.confirmPassword}
                                            error={errors?.confirmPassword}
                                            maxChars={Constants.PASSWORD_LENGTH_MAX}
                                            onChange={onChange}
                                        />
                                    </Grid>
                                </Grid2>
                                <Grid2 container direction="row" spacing={2} width="100%">
                                    <Grid2 size={{ sm: 6, md: 4, lg: 3 }}>
                                        <ButtonEx
                                            label={
                                                status === "saving" ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                        t(AuthI18N.userSignup.action.submit)
                                                )
                                            }
                                            type="submit"
                                            fullWidth
                                            onClick={handleSubmit}
                                            sx={{ mt: 3, mb: 2 }}
                                        />
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        )}
                    </CardEx>
                </Grid2>
            </Grid2>
        </AppFrame>
    );
};
